import { useEffect, useRef } from "react";
import classNames from "classnames";

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-3xl",
  xl: "max-w-5xl",
  "2xl": "max-w-7xl",
  full: "max-w-[calc(100vw-48px)]",
};

const SkeletonLine = ({ width = "100%", height = 14, className = "" }) => (
  <div
    className={classNames("rounded-md bg-gray-200 animate-pulse", className)}
    style={{ width, height }}
  />
);

const SkeletonBlock = () => (
  <div className="space-y-5">
    <div className="space-y-2">
      <SkeletonLine width="25%" height={12} />
      <SkeletonLine height={36} />
    </div>
    <div className="grid grid-cols-2 gap-5">
      <div className="space-y-2">
        <SkeletonLine width="30%" height={12} />
        <SkeletonLine height={36} />
      </div>
      <div className="space-y-2">
        <SkeletonLine width="35%" height={12} />
        <SkeletonLine height={36} />
      </div>
    </div>
    <div className="space-y-2">
      <SkeletonLine width="20%" height={12} />
      <SkeletonLine height={36} />
    </div>
    <div className="space-y-2">
      <SkeletonLine width="28%" height={12} />
      <SkeletonLine height={80} />
    </div>
    <div className="grid grid-cols-2 gap-5">
      <div className="space-y-2">
        <SkeletonLine width="32%" height={12} />
        <SkeletonLine height={36} />
      </div>
      <div className="space-y-2">
        <SkeletonLine width="28%" height={12} />
        <SkeletonLine height={36} />
      </div>
    </div>
    <div className="space-y-2">
      <SkeletonLine width="22%" height={12} />
      <SkeletonLine height={36} />
    </div>
  </div>
);

export const CModal = ({
  open = false,
  onClose,
  title,
  children,
  onSubmit,
  size = "xl",
  submitLabel = "Lưu",
  loading = false,
  fetching = false,
  footer,
  hideFooter = false,
  className = "",
  parentClassName = "",
}) => {
  const bodyRef = useRef(null);
  const busy = loading || fetching;

  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      if (bodyRef.current) bodyRef.current.scrollTop = 0;

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && open && !busy) onClose?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, busy]);

  return (
    <div
      className={classNames(
        "fixed inset-0 flex items-center justify-center transition-all duration-300",
        open
          ? "z-[1050] pointer-events-auto opacity-100 visible"
          : "-z-[1050] pointer-events-none opacity-0 invisible",
      )}
    >
      {/* Backdrop */}
      <div
        className={classNames(
          "absolute inset-0 bg-black/50 backdrop-blur-[2px] transition-opacity duration-300",
          open ? "opacity-100" : "opacity-0",
        )}
        onClick={() => {
          if (!busy) onClose?.();
        }}
      />

      {/* Modal container */}
      <div
        className={classNames(
          "relative flex flex-col w-full bg-white rounded-xl shadow-[0_25px_80px_rgba(0,0,0,0.25)] overflow-hidden",
          "max-h-[calc(100vh-48px)]",
          "transition-all duration-300",
          open
            ? "scale-100 translate-y-0 opacity-100"
            : "scale-95 translate-y-4 opacity-0",
          sizeClasses[size] || sizeClasses.xl,
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 bg-gradient-to-r from-[#053c7f] to-[#0a4fa3] flex-shrink-0">
          <h5 className="text-white text-lg font-bold uppercase tracking-wide m-0 leading-6 truncate pr-4">
            {title}
          </h5>
          <button
            type="button"
            onClick={() => {
              if (!busy) onClose?.();
            }}
            disabled={busy}
            className={classNames(
              "flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0",
              "bg-white/15 text-white border-none cursor-pointer outline-none",
              "transition-all duration-200 hover:bg-white/25 hover:scale-110",
              "active:scale-95",
              busy &&
                "opacity-50 cursor-not-allowed hover:bg-white/15 hover:scale-100",
            )}
          >
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>

        {/* Body */}
        <div
          ref={bodyRef}
          className="flex-1 overflow-y-auto bg-white p-6 min-h-0"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#c5ced3 #f1f1f1",
          }}
        >
          {fetching ? <SkeletonBlock /> : children}
        </div>

        {/* Footer */}
        {!hideFooter && (
          <div className="flex items-center justify-end gap-3 px-6 py-3 bg-[#f8f9fa] border-t border-[#e9ecef] flex-shrink-0">
            {footer ? (
              footer
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    if (!busy) onClose?.();
                  }}
                  disabled={busy}
                  className={classNames(
                    "px-5 py-2.5 text-sm font-semibold rounded-lg outline-none",
                    "text-[#6c757d] bg-white border border-[#d1d5db]",
                    "transition-all duration-200",
                    "hover:bg-[#f3f4f6] hover:border-[#9ca3af] hover:text-[#374151]",
                    busy &&
                      "opacity-60 cursor-not-allowed hover:bg-white hover:border-[#d1d5db] hover:text-[#6c757d]",
                  )}
                >
                  Đóng
                </button>
                {onSubmit && (
                  <button
                    type="button"
                    onClick={onSubmit}
                    disabled={busy}
                    className={classNames(
                      "px-5 py-2.5 text-sm font-semibold rounded-lg outline-none",
                      "text-white bg-[#053c7f] border border-[#053c7f]",
                      "transition-all duration-200",
                      "hover:bg-[#042d63] hover:shadow-[0_4px_12px_rgba(5,60,127,0.3)]",
                      "active:translate-y-0",
                      busy && "opacity-60 cursor-not-allowed",
                    )}
                  >
                    {busy ? (
                      <span className="flex items-center gap-2">
                        <i className="fa-solid fa-spinner fa-spin text-sm" />
                        {fetching ? "Đang lấy dữ liệu..." : "Đang xử lý..."}
                      </span>
                    ) : (
                      submitLabel
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
